import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { SettingRow } from "@src/appV2/redesign/components/SettingRow";
import { useId, useState } from "react";

import { Text } from "../../components/Text";
import { TextInput } from "../../components/TextInput";

interface SettingNumberInputProps {
  title: string;
  subtitle?: string;
  value: number;
  disabled?: boolean;
  min?: number;
  max?: number;
  unit?: string;
  errorMessage?: string;
  onChange: (value: number) => void;
}

export function SettingNumberInput({
  title,
  subtitle,
  value,
  disabled = false,
  min,
  max,
  unit,
  errorMessage,
  onChange,
}: SettingNumberInputProps) {
  const [localValue, setLocalValue] = useState(String(value));
  const errorMessageId = useId();
  const hasError = isDefined(errorMessage);

  return (
    <Box>
      <SettingRow
        title={title}
        subtitle={subtitle}
        disabled={disabled}
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextInput
              type="number"
              value={localValue}
              disabled={disabled}
              error={hasError}
              inputProps={{
                min,
                max,
                "aria-label": title,
                "aria-describedby": hasError ? errorMessageId : undefined,
              }}
              sx={{ width: 80 }}
              onBlur={() => {
                const parsed = Number(localValue);
                if (localValue !== "" && !Number.isNaN(parsed) && parsed !== value) {
                  onChange(parsed);
                } else {
                  setLocalValue(String(value));
                }
              }}
              onChange={(event) => {
                setLocalValue(event.target.value);
              }}
            />
            {unit && (
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {unit}
              </Text>
            )}
          </Stack>
        }
      />
      {hasError && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Text
            id={errorMessageId}
            role="alert"
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.error.main,
              width: "min(16rem, calc(100vw - 2rem))",
            })}
          >
            {errorMessage}
          </Text>
        </Box>
      )}
    </Box>
  );
}
